import { computed, getCurrentInstance } from 'vue'
import { useMainStore } from '@/store/main'
import { useI18nStore } from '@/store/i18n'
import { useLocationStore } from '@/store/location'
import { useSurveyStore } from '@/store/survey'
import { useUserFlagsStore } from '@/store/userFlags'
import { useCoreFeaturesStore } from '@/store/coreFeatures'
import { useSubscriptionStore } from '@/store/subscription'
import { usePaywallStore } from '@/store/paywall'
import { useFunnelType } from '@/composables/useFunnelType'

/**
 * Provides computed A/B experiment flags and feature flags.
 * Requires $absmartly to be available on the Vue instance (set up by Experiments mixin in App.vue).
 *
 * Usage in setup():
 *   const { IS_CLICK_TO_CANCEL_CALIFORNIA } = useABFeatureFlag()
 */
export function useABFeatureFlag() {
	const instance = getCurrentInstance()
	const proxy = instance?.proxy

	// Helper to access $absmartly treatment. Falls back to 0 if not yet initialized.
	// Reading isAbSmartlyReady.value creates a reactive dependency so that computed flags
	// re-evaluate once the ABSmartly context becomes available.
	const treatment = (experimentName) => {
		if (!isAbSmartlyReady.value) return 0
		return proxy?.$absmartly?.treatment(experimentName) ?? 0
	}

	const mainStore = useMainStore()
	const i18nStore = useI18nStore()
	const locationStore = useLocationStore()
	const surveyStore = useSurveyStore()
	const userFlagsStore = useUserFlagsStore()
	const coreFeaturesStore = useCoreFeaturesStore()
	const subsStore = useSubscriptionStore()
	const paywallStore = usePaywallStore()
	const { AREA } = useFunnelType()

	// Reactive flag that flips when $absmartly context becomes available.
	// Used as a dependency inside treatment() to force computed flags to recompute.
	const isAbSmartlyReady = computed(() => mainStore.isAbSmartlyReady)

	// Store getters as computed refs
	const language = computed(() => i18nStore.getLanguage)
	const accountPlatform = computed(() => locationStore.accountPlatform)
	// getQueryParam is a getter that returns a function: (param) => value
	// We expose it as a plain function so this.getQueryParam('foo') works in Options API.
	const getQueryParam = (param) => locationStore.getQueryParam(param)
	const userBmi = computed(() => mainStore.getBmi)
	const countryCode = computed(() => mainStore.getCountryCode)
	const regionCode = computed(() => mainStore.getRegionCode)
	const getPropertyValue = computed(() => surveyStore.getPropertyValue)
	const isOrganicTrafficOnboarding = computed(() => userFlagsStore.getIsOrganicTrafficOnboarding)
	const FEAT_CONFIRM_EMAIL_BY_CODE = computed(() => coreFeaturesStore.FEAT_CONFIRM_EMAIL_BY_CODE)
	const subscriptionProvider = computed(() => subsStore.checkProvider)
	const subscriptionPeriodType = computed(() => subsStore.checkPeriodType)
	const canDowngradeSubscription = computed(() => subsStore.canDowngrade)
	const lastUsedProvider = computed(() => paywallStore.getLastUsedProvider)
	const subscription = computed(() => subsStore.getSubscription)
	const userGeo = computed(() => mainStore.getGeo)

	// --- Experiment flags ---

	const IS_SIMPLE_WEB_APP_MVP_EXP = computed(() => {
		return treatment('web_simple_mvp_launch')
	})
	const IS_ACCOUNT_DELETION_DEEPLINK_V2_EXP = computed(() => {
		return getQueryParam('exp')?.includes?.('auth0_rollout_deletion_v2')
	})
	const IS_AUTH0_ACCOUNT_DELETION_DEEPLINK_ACCOUNT_DELETION = computed(() => {
		return getQueryParam('exp')?.includes?.('auth0_rollout_deletion')
	})
	const IS_ACCOUNT_SIMPLE_MOVE = computed(() => {
		// `exp=simple_moves` — in-app cancellation deeplink (DEV-33263); `is_moves_user` — CRM
		// cancel-offer email links (DEV-33344). Read like any other query param.
		return getQueryParam('exp')?.includes?.('simple_moves') || !!getQueryParam('is_moves_user')
	})
	const IS_CONFIRM_EMAIL_BY_CODE_EXP = computed(() => {
		return (accountPlatform.value.ios || accountPlatform.value.android) && FEAT_CONFIRM_EMAIL_BY_CODE.value
	})
	const IS_CLICK_TO_CANCEL_CALIFORNIA = computed(() => {
		return regionCode.value?.toLowerCase?.() === 'ca'
	})
	const IS_WARMUP_BEFORE_WINBACK_OFFER_EXP = computed(() => {
		if (subscriptionPeriodType.value.isCommitment || countryCode.value === 'us') {
			return false
		}
		if (language.value === 'en') {
			return true
		}
		return Boolean(treatment('web_warmup_before_winback_offer'))
	})
	const IS_QUESTION_FOR_CEO_EXP = computed(() => {
		return countryCode.value === 'us'
	})
	const IS_PHONE_NUMBERS_ON_THE_CONFIRMATION_SCREEN_EXP = computed(() => {
		return treatment('web_phone_numbers_on_the_confirmation_screen')
	})
	const IS_SHORT_PLAN_DOWNGRADE_EXP = computed(() => {
		return canDowngradeSubscription.value && treatment('web_short_plan_downgrade')
	})
	const IS_PIX_PAYMENT_METHOD_FOR_BR_EXP = computed(() => {
		if (subscription.value?.payment_method === 'pix_automatico') {
			return true
		}
		return treatment('web_pix_payment_method_for_br')
	})
	const IS_CHAIR_WORKOUTS_MALE_LOADER_TEASER_EXP = computed(() => {
		return treatment('web_chair_workouts_male_loader_teaser')
	})
	const IS_FIRST_AND_LAST_NAME_IN_THE_PAYMENT_FORM_EXP = computed(() => {
		return Boolean(treatment('web_first_and_last_name_in_the_payment_form'))
	})
	const IS_ZIP_CODE_IN_PAYMENT_FORM_EXP = computed(() => {
		return Boolean(treatment('web_zip_code_in_the_payment_form'))
	})
	const IS_DOWNLOAD_INVITE_CONFIRMATION_SCREEN_EXP_B = computed(() => {
		return treatment('web_download_invite_confirmation_screen') === 1
	})
	const IS_DOWNLOAD_INVITE_CONFIRMATION_SCREEN_EXP_C = computed(() => {
		return treatment('web_download_invite_confirmation_screen') === 2
	})
	const IS_AI_SLIM_FACE_FEMALE_B_EXP = computed(() => {
		// /result_complete?safe=true&host=simple.life&country=US&lang=en&utm_source=dashboard&exp=web_ai_slim_face_female~1&area=female&mock-data=true
		return treatment('web_ai_slim_face_female') === 1
	})
	const IS_AI_SLIM_FACE_FEMALE_C_EXP = computed(() => {
		// /result_complete?safe=true&host=simple.life&country=US&lang=en&utm_source=dashboard&exp=web_ai_slim_face_female~2&area=female&mock-data=true
		return treatment('web_ai_slim_face_female') === 2
	})
	const IS_BLURRED_RESULT_EMAIL_CAPTURE_EXP = computed(() => {
		return treatment('web_blurred_result_email_capture')
	})
	const IS_TAI_CHI_FEMALE_V2_MOTO_BLOCK_EXP = computed(() => {
		return treatment('web_tai_chi_female_v2_moto_block')
	})
	const IS_FEMALE_TAI_CHI_WALKING_V2_FR_EXP = computed(() => {
		return treatment('web_female_tai_chi_walking_v2_fr')
	})
	const IS_FEMALE_TAI_CHI_WALKING_V2_DE_EXP = computed(() => {
		return treatment('web_female_tai_chi_walking_v2_de')
	})
	const IS_WALKING_GS_INDOOR_INTENT_EXP = computed(() => {
		return treatment('web_walking_gs_indoor_intent')
	})
	const IS_MAIN_YOUNGER_AGE_GROUPS_EXP = computed(() => {
		return treatment('web_main_younger_age_groups')
	})
	const IS_REFACTOR_RESULT_COMPLETE_EXP = computed(() => {
		return treatment('web_refactor_result_complete')
	})
	const IS_UPDATE_COOKIE_GDPR_BANNER_EXP = computed(() => {
		return treatment('web_update_cookie_gdpr_banner')
	})
	const IS_POST_PURCHASE_ACTIVATION_EXP_B = computed(() => {
		return treatment('web_print_tai_chi_f_post_purchase_activation') === 1
	})
	const IS_POST_PURCHASE_ACTIVATION_EXP_C = computed(() => {
		return treatment('web_print_tai_chi_f_post_purchase_activation') === 2
	})
	const IS_APP_LOGIN_PDF_REFRAMING_B = computed(() => {
		return treatment('web_app_login_pdf_reframing') === 1
	})
	const IS_APP_LOGIN_PDF_REFRAMING_C = computed(() => {
		return treatment('web_app_login_pdf_reframing') === 2
	})
	const IS_BULK_COPY_CANCELLATION_EXP = computed(() => {
		if (IS_ACCOUNT_SIMPLE_MOVE.value) return false
		return treatment('web_bulk_copy_cancellation') === 1
	})
	const IS_D1_REMINDER_CANCELLATION_EXP = computed(() => {
		return treatment('web_d1_reminder_cancellation') === 1
	})
	const IS_TAI_CHI_WALKING_FEM_V2_TEASER_W_PEOPLE_JOINED_EXP = computed(() => {
		return treatment('web_tai_chi_female_progress_teaser')
	})

	const IS_MALE_PAYWALL_REDESIGN_EXP = computed(() => {
		return treatment('web_male_paywall_redesign') === 1
	})

	const IS_PRIMER_PAYPAL_VS_NATIVE = computed(() => {
		if (subscriptionProvider.value.isPaypal) {
			return true
		}
		return treatment('web_primer_paypal_vs_native') === 1
	})

	const IS_WATER_FASTING_PR_GUIDE_EXP = computed(() => {
		return treatment('web_water_fasting_pr_guide')
	})

	// Applovin / Free Guide flow, rolled out for EN (DEV-34982). The appArea list is explicit because
	// the experiment's scope lived in the ABsmartly audience, not in code. Non-EN keeps the previously
	// translated copy until the new strings are localized (DEV-34981). Areas, in order:
	// activity-male-chair-workouts-al, activity-male-chair-tai-chi-al, activity-male-tai-chi-yoga-al,
	// activity-female-tai-chi-walking-v2-al, activity-male-tai-chi-walking-al.
	const IS_APPLOVIN_FREE_GUIDE_FLOW = computed(() => {
		return (
			(AREA.value.CHAIR_WORKOUTS_MALE_AL ||
				AREA.value.CHAIR_TAI_CHI_MALE_AL ||
				AREA.value.YOGA_TAI_CHI_MALE_AL ||
				AREA.value.WALKING_TAI_CHI_FEMALE_V2_AL ||
				AREA.value.WALKING_TAI_CHI_MALE_AL) &&
			language.value === 'en'
		)
	})

	const IS_REDIRECT_SIMPLE_MOVES_EXP = computed(() => {
		return treatment('web_redirect_simple_moves') === 1
	})

	const IS_STRIPE_DIRECT_INTEGRATION_EXP = computed(() => {
		if (subscriptionProvider.value.isStripe) {
			return true
		}
		return treatment('web_stripe_direct_integration') === 1
	})

	const IS_CANCEL_FLOW_COMPLIANCE_CLEANUP_EXP = computed(() => {
		return treatment('web_cancel_flow_compliance_cleanup') === 1
	})

	const IS_BACKEND_PRODUCTS_BUNDLE_EXP = computed(() => {
		return treatment('web_backend_products_bundle') === 1
	})

	return {
		language,
		accountPlatform,
		getQueryParam,
		userBmi,
		countryCode,
		regionCode,
		getPropertyValue,
		isOrganicTrafficOnboarding,
		FEAT_CONFIRM_EMAIL_BY_CODE,
		subscriptionProvider,
		subscriptionPeriodType,
		canDowngradeSubscription,
		lastUsedProvider,
		subscription,
		userGeo,

		// Experiment flags
		IS_AI_SLIM_FACE_FEMALE_B_EXP,
		IS_AI_SLIM_FACE_FEMALE_C_EXP,
		IS_SIMPLE_WEB_APP_MVP_EXP,
		IS_ACCOUNT_DELETION_DEEPLINK_V2_EXP,
		IS_AUTH0_ACCOUNT_DELETION_DEEPLINK_ACCOUNT_DELETION,
		IS_ACCOUNT_SIMPLE_MOVE,
		IS_CONFIRM_EMAIL_BY_CODE_EXP,
		IS_CLICK_TO_CANCEL_CALIFORNIA,
		IS_WARMUP_BEFORE_WINBACK_OFFER_EXP,
		IS_QUESTION_FOR_CEO_EXP,
		IS_PHONE_NUMBERS_ON_THE_CONFIRMATION_SCREEN_EXP,
		IS_SHORT_PLAN_DOWNGRADE_EXP,
		IS_PIX_PAYMENT_METHOD_FOR_BR_EXP,
		IS_CHAIR_WORKOUTS_MALE_LOADER_TEASER_EXP,
		IS_FIRST_AND_LAST_NAME_IN_THE_PAYMENT_FORM_EXP,
		IS_ZIP_CODE_IN_PAYMENT_FORM_EXP,
		IS_DOWNLOAD_INVITE_CONFIRMATION_SCREEN_EXP_B,
		IS_DOWNLOAD_INVITE_CONFIRMATION_SCREEN_EXP_C,
		IS_BLURRED_RESULT_EMAIL_CAPTURE_EXP,
		IS_TAI_CHI_FEMALE_V2_MOTO_BLOCK_EXP,
		IS_FEMALE_TAI_CHI_WALKING_V2_FR_EXP,
		IS_FEMALE_TAI_CHI_WALKING_V2_DE_EXP,
		IS_WALKING_GS_INDOOR_INTENT_EXP,
		IS_MAIN_YOUNGER_AGE_GROUPS_EXP,
		IS_REFACTOR_RESULT_COMPLETE_EXP,
		IS_UPDATE_COOKIE_GDPR_BANNER_EXP,
		IS_POST_PURCHASE_ACTIVATION_EXP_B,
		IS_POST_PURCHASE_ACTIVATION_EXP_C,
		IS_APP_LOGIN_PDF_REFRAMING_B,
		IS_APP_LOGIN_PDF_REFRAMING_C,
		IS_BULK_COPY_CANCELLATION_EXP,
		IS_PRIMER_PAYPAL_VS_NATIVE,
		IS_D1_REMINDER_CANCELLATION_EXP,
		IS_TAI_CHI_WALKING_FEM_V2_TEASER_W_PEOPLE_JOINED_EXP,
		IS_MALE_PAYWALL_REDESIGN_EXP,
		IS_WATER_FASTING_PR_GUIDE_EXP,
		IS_APPLOVIN_FREE_GUIDE_FLOW,
		IS_REDIRECT_SIMPLE_MOVES_EXP,
		IS_STRIPE_DIRECT_INTEGRATION_EXP,
		IS_CANCEL_FLOW_COMPLIANCE_CLEANUP_EXP,
		IS_BACKEND_PRODUCTS_BUNDLE_EXP,
	}
}
